import ToggleGroup, {
  ToggleGroupOption,
} from "@/components/toggle-group"
import { Label } from "@/components/ui/label"
import { Input, ErrorFlagState } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import {
  formatTimeWindowForDisplay,
  getWindowErrorFlagState,
} from "@/packaging-v2/lib/utils"
import { V2ParamFormData } from "@/store/packaging-v2"
import { FormParamStatus } from "@/lib/form/types"
import { V2InputType } from "@/packaging-v2/lib/schema"
import { Icon } from "@/icons/icon"

export interface FrequencyWidgetProps {
  className?: string
  param: V2ParamFormData
  disabled?: boolean
  onWindowChange?: (windowIndex: number, value: string) => void
  onClickErrorFlag?: (windowIndex: number) => void
}

/**
 * Widget for displaying and editing time-windowed frequency parameters
 * Supports CHOICE, VALUE_TEXT, and VALUE_NUMBER input types
 */
export default function FrequencyWidget({
  className,
  param,
  disabled = false,
  onWindowChange,
  onClickErrorFlag,
}: FrequencyWidgetProps) {
  function handleChange(windowIndex: number, value: string) {
    if (onWindowChange) {
      onWindowChange(windowIndex, value)
    }
  }

  if (!param.windows || param.windows.length === 0) {
    return null
  }

  const labelText = `${param.name}${param.unit ? ` (${param.unit})` : ""}`
  return (
    <div className={cn("space-y-2", className)}>
      {/* Label on top */}
      <Label className="text-sm font-medium truncate" title={labelText}>
        {param.name}
        {param.unit && (
          <span className="text-gray-500">({param.unit})</span>
        )}
      </Label>

      {/* Windows grid below */}
      <div className="grid grid-cols-2 gap-3">
        {param.windows.map((window, index) => {
          return (
            <div
              key={window.id}
              className="flex flex-col rounded-lg border p-2 border-gray-200"
            >
              {/* Value input - render based on input type */}
              <div className="mb-2">
                {param.status === FormParamStatus.SUBMITTED && window.value ? (
                  <span className="font-medium text-sm">
                    {window.value}
                    {param.inputType === V2InputType.VALUE_NUMBER && param.unit && ` ${param.unit}`}
                  </span>
                ) : (
                  <WindowInput
                    param={param}
                    window={window}
                    disabled={disabled}
                    onChange={(value) => handleChange(index, value)}
                    onClickErrorFlag={() => onClickErrorFlag?.(index)}
                  />
                )}
              </div>

              {/* Time slot label below */}
              <div className="text-xs text-gray-500 text-center">
                {formatTimeWindowForDisplay(window.startTime, window.endTime)}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Individual window input component that renders different input types
 */
interface WindowInputProps {
  param: V2ParamFormData
  window: { id: string; value?: string; startTime: string; endTime: string; recordedAt?: string; remark?: string }
  disabled: boolean
  onChange: (value: string) => void
  onClickErrorFlag: () => void
}

function WindowInput({
  param,
  window,
  disabled,
  onChange,
  onClickErrorFlag,
}: WindowInputProps) {
  // For CHOICE input type, use ToggleGroup
  if (param.inputType === V2InputType.CHOICE) {
    const options: ToggleGroupOption[] =
      param.possibleValues && param.possibleValues.length > 0
        ? param.possibleValues.map((v) => ({ label: v, value: v }))
        : [
            { label: "Pass", value: "Pass" },
            { label: "Fail", value: "Fail" },
          ]
    const correctValue = param.correctValue || "Pass"

    return (
      <ToggleGroup
        options={options}
        value={window.value}
        correctValue={correctValue}
        disabled={disabled}
        flagState={getWindowErrorFlagState(param, window)}
        onChange={onChange}
        onClickRedFlag={onClickErrorFlag}
      />
    )
  }

  // For VALUE_NUMBER input type, use number input with limit validation
  if (param.inputType === V2InputType.VALUE_NUMBER) {
    const errorFlagState = getWindowErrorFlagState(param, window)
    const hasError = errorFlagState !== ErrorFlagState.None

    return (
      <div className="relative">
        <Input
          type="text"
          inputMode="numeric"
          value={window.value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            hasError && "border-red-500 pr-10"
          )}
        />
        {hasError && (
          <button
            type="button"
            className="absolute top-1/2 right-3 -translate-y-1/2 hover:cursor-pointer"
            onClick={onClickErrorFlag}
          >
            <Icon
              name={window.remark ? "RedFlag" : "FlagNeutral"}
              className="h-4 w-4"
            />
          </button>
        )}
      </div>
    )
  }

  // For VALUE_TEXT input type, use text input
  if (param.inputType === V2InputType.VALUE_TEXT) {
    return (
      <Input
        type="text"
        value={window.value || ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    )
  }

  // Default fallback (should not happen)
  return (
    <Input
      type="text"
      value={window.value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/**
 * Compact version for displaying in read-only mode
 */
export function FrequencyWidgetReadOnly({
  className,
  param,
}: {
  className?: string
  param: V2ParamFormData
}) {
  if (!param.windows || param.windows.length === 0) {
    return null
  }

  return (
    <div className={cn("space-y-2", className)}>
      <Label className="text-sm font-medium truncate" title={param.name}>{param.name}</Label>

      <div className="grid grid-cols-2 gap-2">
        {param.windows.map((window) => (
          <div
            key={window.id}
            className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded"
          >
            <span className="text-gray-600">
              {formatTimeWindowForDisplay(window.startTime, window.endTime)}
            </span>
            <span className="font-medium">{window.value || "-"}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
